import * as React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Popper,
    TextField,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import BoltIcon from '@mui/icons-material/Bolt';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { nanoid } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import { format, isBefore } from 'date-fns';
import { he } from 'date-fns/locale';

import { getSpecificCustomer, selectCustomerById, selectCustomersidname } from '../../store/customers/customersSlice';
import { subStringMatch } from '../../utils/sort';
import { SNACKBAR_OPEN } from '../../store/actions';
import { CUSTOMER_DOCUMENTS } from '../../hooks/customer/documents.hooks';
import { createRealCustomerDocuments, getClientChecks, getSpecificDocument, calculateOptimized } from '../../api/customer/documents.api';
import { dateObjectToString, roundDecimals } from '../../fromKotlin/nkutils';
import {
    applyCheckMemory,
    Check,
    Details,
    createDocument,
    Payment,
    PaymentMethod,
    rememberedCheckFields
} from '../../types/DocumentsTypes/utils';
import DocumentPreview from '../../views/customer/profile/documents/DocumentPreview';
import DocumentCustomerDetails, {
    BusinessIdentityMode,
    resolveDocumentDetails
} from '../../views/customer/profile/documents/DocumentCustomerDetails';
import { PaymentComp } from '../../views/customer/profile/documents/receipt/NewReceipt';
import CartesetList from '../../views/customer/profile/debts';
import { displayDocumentId, ExtendedDocument } from '../../types/DocumentsTypes/Document';
import type { TaxNote } from '../../types/DocumentsTypes/TaxNote';
import type { RootState } from '../../store';

const styles = () => ({
    popper: {
        maxWidth: 'fit-content'
    }
});
const PopperMy = function (props) {
    return <Popper {...props} style={styles.popper} placement="bottom-start" />;
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const QUICK_RECEIPT_PREFS_KEY = 'quickReceiptPrefs';

/** Only payment method is remembered — date always stays "today". */
function loadPrefs(): { method: PaymentMethod } {
    try {
        const raw = localStorage.getItem(QUICK_RECEIPT_PREFS_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            const method = (parsed.method as PaymentMethod) || 'cash';
            return { method };
        }
    } catch {
        /* ignore */
    }
    return { method: 'cash' };
}

function savePrefs(method: PaymentMethod) {
    try {
        localStorage.setItem(QUICK_RECEIPT_PREFS_KEY, JSON.stringify({ method }));
    } catch {
        /* ignore */
    }
}

const EMPTY_DETAILS: Details = {
    id: 0,
    companyName: '',
    companyId: '',
    city: '',
    street: '',
    streetNumber: '',
    phone: '',
    email: ''
};

/** Apply last-used check bank/account/branch onto a payment line (same rule as NewReceipt). */
function withCheckDefaults(payment: Payment, defs?: Check): Payment {
    return applyCheckMemory(payment, defs);
}

function invoiceOptionLabel(option: TaxNote) {
    const sign = option.type === 'invoice' || option.type === 'sup_invoice' ? 1 : -1;
    return `${option.name || option.title} ${displayDocumentId(option)} - ${option.entName} - ${format(option.date, 'PPP', { locale: he })}  - ${option.value_left * sign} ש''ח `;
}

function invoicesTotal(receipts: TaxNote[]) {
    return roundDecimals(
        receipts.reduce(
            (acc, p) => acc + (p.type === 'invoice' || p.type === 'sup_invoice' ? p.value_left : -p.value_left),
            0
        ),
        2
    );
}

function makePayment(
    method: PaymentMethod,
    amount: number,
    checkDefaults?: Check,
    current?: Check,
    existingId?: string
): Payment {
    return {
        id: existingId ?? nanoid(),
        amount,
        method,
        check: rememberedCheckFields(checkDefaults, {
            ...current,
            dueDate: current?.dueDate ?? new Date(),
        }),
    };
}

interface CreatedEntry {
    key: string;
    doc: ExtendedDocument;
    clientName: string;
    total: number;
}

function QuickReceiptDialog({
    open,
    onClose,
    initialClient
}: {
    open: boolean;
    onClose: () => void;
    initialClient?: any;
}) {
    const dispatch = useDispatch();
    const cache = useQueryClient();
    const customers: any[] = useSelector(selectCustomersidname);
    const externalIdSearch = parseInt(localStorage.getItem('externalIdOrders') ?? '0') === 1;

    const initialPrefs = React.useMemo(() => loadPrefs(), []);
    const [date, setDate] = React.useState<Date>(() => new Date());
    const preferredMethodRef = React.useRef<PaymentMethod>(initialPrefs.method);
    const [client, setClient] = React.useState<any>(null);
    const seededOpenRef = React.useRef(false);
    const [checkDefaults, setCheckDefaults] = React.useState<Check | undefined>();
    const checkDefaultsRef = React.useRef<Check | undefined>(undefined);
    checkDefaultsRef.current = checkDefaults;
    const [details, setDetails] = React.useState<Details>(EMPTY_DETAILS);
    const [identityMode, setIdentityMode] = React.useState<BusinessIdentityMode>('AUTO');
    const [comment, setComment] = React.useState('');
    const [payments, setPayments] = React.useState<Payment[]>(() => [makePayment(initialPrefs.method, 0)]);
    const [taxNotes, setTaxNotes] = React.useState<TaxNote[]>([]);
    const [receipts, setReceipts] = React.useState<TaxNote[]>([]);
    const [invoicesLoading, setInvoicesLoading] = React.useState(false);
    const [selectAllOpen, setSelectAllOpen] = React.useState(false);
    const [creating, setCreating] = React.useState(false);
    const [createdDocs, setCreatedDocs] = React.useState<CreatedEntry[]>([]);
    const [previewDoc, setPreviewDoc] = React.useState<ExtendedDocument | null>(null);
    const [images, setImages] = React.useState<{ id: string; file: File }[] | null>(null);
    const [creditRecepitValue, setCreditRecepitValue] = React.useState(false);
    const [totalDebtExist, setDebtExist] = React.useState<number | undefined>(undefined);
    const [openCustomerArchive, setOpenCustomerArchive] = React.useState(false);

    const theme = useTheme();
    const openCustomerArchiveHandler = React.useCallback(() => setOpenCustomerArchive((p) => !p), []);

    const clientInputRef = React.useRef<HTMLInputElement | null>(null);
    const paymentsUserEditedRef = React.useRef(false);

    const fullCustomer = useSelector((state: RootState) =>
        client?.id != null ? selectCustomerById(state, client.id) : undefined
    );

    React.useEffect(() => {
        const method = payments[0]?.method ?? preferredMethodRef.current;
        preferredMethodRef.current = method;
        savePrefs(method);
    }, [payments]);

    // Fill business details once the full customer is in the store (same source as NewReceipt / NewInvoice).
    React.useEffect(() => {
        if (!fullCustomer) {
            if (client?.id) dispatch(getSpecificCustomer(Number(client.id)));
            setDetails(EMPTY_DETAILS);
            return;
        }
        setDetails(resolveDocumentDetails(fullCustomer, identityMode));
    }, [fullCustomer, identityMode, client?.id, dispatch]);

    React.useEffect(() => {
        if (!fullCustomer) {
            setDebtExist(undefined);
            return;
        }
        const cartesetId = Number(fullCustomer.nkObject?.masterBranch?.id ?? fullCustomer.nkObject?.id);
        if (!Number.isFinite(cartesetId)) {
            setDebtExist(undefined);
            return;
        }
        let cancelled = false;
        calculateOptimized(null, date, cartesetId, false)
            .then((x) => {
                if (!cancelled) setDebtExist(x ?? undefined);
            })
            .catch(() => {
                if (!cancelled) setDebtExist(undefined);
            });
        return () => {
            cancelled = true;
        };
    }, [fullCustomer, date]);

    React.useEffect(() => {
        if (!client?.id) {
            setCheckDefaults(undefined);
            setTaxNotes([]);
            setReceipts([]);
            setSelectAllOpen(false);
            setIdentityMode('AUTO');
            setDetails(EMPTY_DETAILS);
            setCreditRecepitValue(false);
            setOpenCustomerArchive(false);
            return;
        }
        if (!fullCustomer) {
            dispatch(getSpecificCustomer(Number(client.id)));
        }
        let cancelled = false;
        setInvoicesLoading(true);
        setReceipts([]);
        setSelectAllOpen(false);
        setCreditRecepitValue(false);
        paymentsUserEditedRef.current = false;
        setPayments((prev) => [
            makePayment(prev[0]?.method ?? preferredMethodRef.current, 0)
        ]);

        Promise.all([
            getClientChecks(Number(client.id)),
            getSpecificDocument('invoice', Number(client.id), undefined, undefined, true, undefined, true),
            getSpecificDocument('refund', Number(client.id), undefined, undefined, true, undefined, true)
        ])
            .then(([checks, invoices, refunds]) => {
                if (cancelled) return;
                const defs = checks?.check;
                checkDefaultsRef.current = defs;
                setCheckDefaults(defs);
                const openNotes = [...(invoices || []), ...(refunds || [])].filter(
                    (s) =>
                        (s.status === 'open' || s.status === 'partly_closed') &&
                        (s.type === 'invoice' || s.type === 'refund')
                ) as TaxNote[];
                setTaxNotes(openNotes);
                // Same as NewReceipt: write last check bank/account/branch onto payment lines that have no account yet.
                setPayments((prev) => prev.map((p) => withCheckDefaults(p, defs)));
            })
            .catch(() => {
                if (!cancelled) {
                    checkDefaultsRef.current = undefined;
                    setCheckDefaults(undefined);
                    setTaxNotes([]);
                }
            })
            .finally(() => {
                if (!cancelled) setInvoicesLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client?.id, dispatch]);

    const receiptsPaymentSyncKey = React.useMemo(
        () =>
            receipts.length === 0
                ? 'empty'
                : `${receipts.map((r) => `${r.id}:${r.value_left}`).sort().join('|')}_${invoicesTotal(receipts)}`,
        [receipts]
    );

    React.useEffect(() => {
        if (paymentsUserEditedRef.current) return;
        if (receipts.length === 0) {
            setPayments((prev) => [
                makePayment(
                    prev[0]?.method ?? preferredMethodRef.current,
                    0,
                    checkDefaultsRef.current,
                    prev[0]?.check,
                    prev[0]?.id
                )
            ]);
            return;
        }
        const total = invoicesTotal(receipts);
        setPayments((prev) => {
            const hasWithholding = prev.some((p) => p.method === 'withholding_tax');
            if (hasWithholding) {
                return prev.map((payment) => {
                    if (payment.method !== 'withholding_tax') return payment;
                    const percent = payment.check?.percent ?? 0;
                    return {
                        ...payment,
                        amount: roundDecimals((total * percent) / 100, 2),
                        check: { ...payment.check, baseAmount: total, percent }
                    };
                });
            }
            return [
                makePayment(
                    prev[0]?.method ?? preferredMethodRef.current,
                    total,
                    checkDefaultsRef.current,
                    prev[0]?.check,
                    prev[0]?.id
                )
            ];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [receiptsPaymentSyncKey]);

    const resetEntry = React.useCallback(() => {
        setClient(null);
        setComment('');
        setReceipts([]);
        setTaxNotes([]);
        setSelectAllOpen(false);
        setImages(null);
        setDetails(EMPTY_DETAILS);
        setIdentityMode('AUTO');
        checkDefaultsRef.current = undefined;
        setCheckDefaults(undefined);
        paymentsUserEditedRef.current = false;
        setPayments([makePayment(preferredMethodRef.current, 0)]);
        setCreditRecepitValue(false);
        setDebtExist(undefined);
        setOpenCustomerArchive(false);
    }, []);

    const handleClose = React.useCallback(() => {
        if (creating) return;
        onClose();
        setCreatedDocs([]);
        resetEntry();
        seededOpenRef.current = false;
    }, [creating, onClose, resetEntry]);

    React.useEffect(() => {
        if (!open) {
            seededOpenRef.current = false;
            return;
        }
        if (seededOpenRef.current) return;
        seededOpenRef.current = true;
        setDate(new Date());
        if (initialClient) {
            setClient(initialClient);
            paymentsUserEditedRef.current = false;
        } else {
            setTimeout(() => clientInputRef.current?.focus(), 50);
        }
    }, [open, initialClient]);

    const filterClientOptions = React.useCallback(
        (options, { inputValue }) => {
            return options
                .filter((option) => subStringMatch(inputValue, [option.name, externalIdSearch ? option.externalId : option.id]))
                .sort((a, b) => {
                    const n1 = externalIdSearch ? a.externalId : a.id;
                    const n2 = externalIdSearch ? b.externalId : b.id;
                    if (Number(n1) && Number(n2)) return Number(n1) > Number(n2) ? 1 : -1;
                    if (Number(n1)) return 1;
                    if (Number(n2)) return -1;
                    return n1 > n2 ? 1 : -1;
                })
                .slice(0, 100);
        },
        [externalIdSearch]
    );

    const markPaymentsUserEdited = React.useCallback(() => {
        paymentsUserEditedRef.current = true;
    }, []);

    const onPaymentChange = React.useCallback(
        (payment: Payment) => {
            markPaymentsUserEdited();
            preferredMethodRef.current = payment.method;
            setPayments((prev) =>
                prev.map((pmt) => {
                    if (payment.id !== pmt.id) return pmt;
                    const switchedToBank =
                        payment.method !== pmt.method &&
                        (payment.method === 'check' || payment.method === 'fastCheck' || payment.method === 'deposit');
                    return switchedToBank ? withCheckDefaults(payment, checkDefaultsRef.current) : payment;
                })
            );
        },
        [markPaymentsUserEdited]
    );

    const onPaymentAdd = React.useCallback(() => {
        markPaymentsUserEdited();
        setPayments((prev) => {
            const total = invoicesTotal(receipts);
            const paidSoFar = roundDecimals(
                prev.reduce((acc, p) => acc + p.amount, 0),
                2
            );
            const remainder = Math.max(0, roundDecimals(total - paidSoFar, 2));
            return prev.concat(
                makePayment(
                    prev[prev.length - 1]?.method ?? preferredMethodRef.current,
                    remainder,
                    checkDefaultsRef.current,
                    prev[prev.length - 1]?.check
                )
            );
        });
    }, [markPaymentsUserEdited, receipts]);

    const onPaymentDelete = React.useCallback(
        (pid: string) => {
            markPaymentsUserEdited();
            setPayments((prev) => prev.filter((p) => p.id !== pid));
        },
        [markPaymentsUserEdited]
    );

    const paymentTotal = React.useMemo(
        () => roundDecimals(payments.reduce((acc, p) => acc + (p.amount || 0), 0), 4),
        [payments]
    );

    const invoiceSelectedTotal = React.useMemo(() => invoicesTotal(receipts), [receipts]);
    const remainingToCredit = React.useMemo(
        () => roundDecimals(invoiceSelectedTotal - paymentTotal, 2),
        [invoiceSelectedTotal, paymentTotal]
    );

    const canCreate =
        Boolean(client) &&
        Boolean(fullCustomer) &&
        details.id > 0 &&
        paymentTotal > 0 &&
        !creating &&
        !(receipts.length === 1 && receipts[0].type === 'refund');

    const handleCreate = React.useCallback(
        async (continueAfter: boolean) => {
            if (!client || !fullCustomer || details.id <= 0 || paymentTotal <= 0) return;
            if (receipts.length === 1 && receipts[0].type === 'refund') return;
            setCreating(true);
            try {
                const doc = createDocument({
                    comment,
                    mainValue: paymentTotal,
                    details,
                    date,
                    type: 'receipt',
                    receiptsIds: receipts.map((d) =>
                        d.type === 'invoice' || d.type === 'sup_invoice' ? d.id : -d.id
                    ),
                    payments,
                    entId: String(client.id),
                    status: 'approved',
                    refundRest: creditRecepitValue && remainingToCredit > 0
                });

                const created = await createRealCustomerDocuments(String(client.id), doc);
                cache.invalidateQueries(CUSTOMER_DOCUMENTS);
                const createdDoc = (Array.isArray(created) ? created[0] : created) as ExtendedDocument;
                dispatch({
                    type: SNACKBAR_OPEN,
                    open: true,
                    message: 'הקבלה נוצרה בהצלחה!',
                    variant: 'alert',
                    alertSeverity: 'success'
                });
                setCreatedDocs((prev) =>
                    [
                        {
                            key: `${createdDoc?.id ?? Date.now()}-${prev.length}`,
                            doc: createdDoc,
                            clientName: client.name,
                            total: paymentTotal
                        },
                        ...prev
                    ].slice(0, 20)
                );

                if (continueAfter) {
                    resetEntry();
                    setTimeout(() => clientInputRef.current?.focus(), 50);
                } else {
                    onClose();
                    setCreatedDocs([]);
                    resetEntry();
                }
            } catch (e) {
                console.log(e);
                dispatch({
                    type: SNACKBAR_OPEN,
                    open: true,
                    message: 'אירעה שגיאה ביצירת הקבלה',
                    variant: 'alert',
                    alertSeverity: 'error'
                });
            } finally {
                setCreating(false);
            }
        },
        [client, paymentTotal, receipts, comment, date, payments, details, fullCustomer, dispatch, cache, resetEntry, onClose, creditRecepitValue, remainingToCredit]
    );

    const onDialogKeyDown = React.useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                if (canCreate) void handleCreate(true);
            }
        },
        [canCreate, handleCreate]
    );

    const sortedTaxNotes = React.useMemo(
        () =>
            [...taxNotes].sort((d1, d2) =>
                dateObjectToString(d1.date) === dateObjectToString(d2.date)
                    ? 0
                    : isBefore(d1.date, d2.date)
                      ? 1
                      : -1
            ),
        [taxNotes]
    );

    return (
        <>
            {open ? (
                <Dialog
                    open={open}
                    onClose={() => {
                        if (openCustomerArchive) return;
                        handleClose();
                    }}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BoltIcon sx={{ color: '#ff6f00' }} />
                        הוצאת כמה קבלות ברצף
                        <Box sx={{ flexGrow: 1 }} />
                        <IconButton size="small" onClick={handleClose}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers onKeyDown={onDialogKeyDown}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start', mb: 2 }}>
                            {!fullCustomer ? (
                                <TextField
                                    type="date"
                                    size="small"
                                    label="תאריך"
                                    value={dateObjectToString(date)}
                                    onChange={(e) => {
                                        const [y, m, d] = e.target.value.split('-').map(Number);
                                        if (y && m && d) setDate(new Date(y, m - 1, d));
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ width: 170 }}
                                />
                            ) : null}
                            <Autocomplete
                                value={client}
                                onChange={(e, value) => {
                                    setClient(value);
                                    setIdentityMode('AUTO');
                                }}
                                PopperComponent={PopperMy}
                                options={customers}
                                getOptionLabel={(o: any) => `${externalIdSearch ? o.externalId : o.id} - ${o.name}`}
                                filterOptions={filterClientOptions}
                                size="small"
                                autoHighlight
                                sx={{ minWidth: 260, flexGrow: 1 }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputRef={clientInputRef}
                                        label="חפש לקוח.."
                                        autoFocus
                                        fullWidth
                                        size="small"
                                    />
                                )}
                            />
                            <TextField
                                size="small"
                                label="הערה"
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                sx={{ minWidth: 180, flexGrow: 1 }}
                                disabled={!client}
                            />
                            {client ? (
                                <Chip
                                    size="small"
                                    label="כרטסת לקוח"
                                    variant="outlined"
                                    onClick={openCustomerArchiveHandler}
                                    sx={{
                                        mt: 0.5,
                                        '& .MuiChip-label': { fontWeight: 'bold', color: '#000080' }
                                    }}
                                />
                            ) : null}
                        </Box>

                        {client && fullCustomer ? (
                            <Box sx={{ mb: 2 }}>
                                <DocumentCustomerDetails
                                    customer={fullCustomer}
                                    date={date}
                                    setDate={setDate}
                                    setDetails={setDetails}
                                    details={details}
                                    identityMode={identityMode}
                                    setIdentityMode={setIdentityMode}
                                    setDriver={() => undefined}
                                    drivers={[]}
                                />
                            </Box>
                        ) : null}

                        {typeof totalDebtExist === 'number' ? (
                            <Alert
                                severity={totalDebtExist > 0 ? 'error' : 'info'}
                                sx={{
                                    mb: 2,
                                    backgroundColor: totalDebtExist > 0 ? '#ffebee' : theme.palette.info.light,
                                    '& .MuiAlert-message': { width: '100%' }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                            {totalDebtExist > 0 ? 'חוב כרטסת ללקוח:' : 'יתרת זכות ללקוח:'}
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 'bold',
                                                color: totalDebtExist > 0 ? '#c62828' : theme.palette.info.dark
                                            }}
                                        >
                                            {Math.abs(totalDebtExist).toFixed(2)} ₪
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        onClick={openCustomerArchiveHandler}
                                        sx={{
                                            fontWeight: 'bold',
                                            color: '#000080',
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            whiteSpace: 'nowrap',
                                            '&:hover': { color: '#0000cc' }
                                        }}
                                    >
                                        כרטסת לקוח
                                    </Typography>
                                </Box>
                            </Alert>
                        ) : null}

                        {client ? (
                            <>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={selectAllOpen}
                                            disabled={invoicesLoading || taxNotes.length === 0}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                setSelectAllOpen(checked);
                                                paymentsUserEditedRef.current = false;
                                                setReceipts(
                                                    checked
                                                        ? taxNotes.filter(
                                                              (d) => d.status === 'open' || d.status === 'partly_closed'
                                                          )
                                                        : []
                                                );
                                            }}
                                            color="primary"
                                        />
                                    }
                                    label="בחר את כל חשבוניות המס הפתוחות"
                                    sx={{ mb: 1 }}
                                />

                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    value={receipts}
                                    getOptionLabel={invoiceOptionLabel}
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                    onChange={(event, d) => {
                                        paymentsUserEditedRef.current = false;
                                        setReceipts(d ?? []);
                                        const openOnes = taxNotes.filter(
                                            (n) => n.status === 'open' || n.status === 'partly_closed'
                                        );
                                        setSelectAllOpen(
                                            openOnes.length > 0 &&
                                                openOnes.every((n) => (d ?? []).some((r) => r.id === n.id))
                                        );
                                    }}
                                    options={sortedTaxNotes}
                                    loading={invoicesLoading}
                                    fullWidth
                                    autoHighlight
                                    size="small"
                                    sx={{ mb: 1.5 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="בחר חשבונית מס"
                                            size="small"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {invoicesLoading ? (
                                                            <CircularProgress color="inherit" size={20} />
                                                        ) : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                )
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option, { selected }) => (
                                        <li {...props} key={option.id}>
                                            <Checkbox
                                                icon={icon}
                                                checkedIcon={checkedIcon}
                                                style={{ marginRight: 8 }}
                                                checked={selected}
                                            />
                                            {invoiceOptionLabel(option)}
                                        </li>
                                    )}
                                />

                                {receipts.length > 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                        סכום חשבוניות: ₪{invoicesTotal(receipts)}
                                    </Typography>
                                ) : null}

                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                                    תשלומים
                                </Typography>
                                {payments.map((p, i) => (
                                    <Box key={p.id} sx={{ mb: 2 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            תשלום מס&apos; {i + 1}
                                        </Typography>
                                        <PaymentComp
                                            payment={p}
                                            onPaymentChange={onPaymentChange}
                                            onPaymentAdd={onPaymentAdd}
                                            onPaymentDelete={onPaymentDelete}
                                            addable={i === payments.length - 1}
                                            deletable={i !== 0}
                                            images={images}
                                            setImages={setImages}
                                            customerId={String(client.id)}
                                            customerDetails={details}
                                        />
                                    </Box>
                                ))}

                                {receipts.length > 0 && remainingToCredit > 0 ? (
                                    <FormControlLabel
                                        sx={{ mt: 0.5 }}
                                        control={
                                            <Checkbox
                                                checked={creditRecepitValue}
                                                onChange={(e) => setCreditRecepitValue(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label={`זכה סכום שנותר מהחשבוניות (${remainingToCredit} ש"ח)`}
                                    />
                                ) : null}
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                                בחר לקוח כדי לסמן חשבוניות ולהזין תשלום
                            </Typography>
                        )}

                        {createdDocs.length > 0 ? (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    נוצרו בסשן הזה:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {createdDocs.map((c) => (
                                        <Chip
                                            key={c.key}
                                            size="small"
                                            icon={<VisibilityOutlinedIcon fontSize="small" />}
                                            label={`${c.doc?.name ?? 'קבלה'} ${c.doc?.id ?? ''} · ${c.clientName} · ₪${roundDecimals(c.total, 2)}`}
                                            onClick={() => setPreviewDoc(c.doc)}
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            </Box>
                        ) : null}
                    </DialogContent>
                    <DialogActions>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pl: 1 }}>
                            Ctrl+Enter ליצירה והמשך · Esc לסגירה
                            {paymentTotal > 0 ? ` · סה״כ ₪${paymentTotal}` : ''}
                        </Typography>
                        <Button onClick={handleClose} disabled={creating}>
                            ביטול
                        </Button>
                        <Button onClick={() => handleCreate(false)} disabled={!canCreate} variant="outlined">
                            צור וסגור
                        </Button>
                        <Button onClick={() => handleCreate(true)} disabled={!canCreate} variant="contained" color="success">
                            צור והמשך
                        </Button>
                    </DialogActions>
                </Dialog>
            ) : null}

            {openCustomerArchive && client ? (
                <Dialog
                    fullWidth
                    maxWidth="lg"
                    scroll="body"
                    open={openCustomerArchive}
                    onClose={openCustomerArchiveHandler}
                    onBackdropClick={openCustomerArchiveHandler}
                >
                    <CartesetList cid={String(client.id)} rowsPerPage={10} />
                </Dialog>
            ) : null}

            {previewDoc ? (
                <Dialog fullWidth maxWidth="lg" open onClose={() => setPreviewDoc(null)} sx={{ '& .MuiDialog-paper': { p: 0 } }}>
                    <DocumentPreview document={previewDoc} creditRecepitValue={false} setCreditRecepitValue={() => undefined} />
                </Dialog>
            ) : null}
        </>
    );
}

export default React.memo(QuickReceiptDialog);
